// src/modules/Login/AuthContext.js
import React, { createContext, useContext, useEffect, useState } from 'react';
import config from '../../config/config';
import { msalInstance } from '../../config/authConfig';

const apiUrl = config.apiBaseUrl;
const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(
    () => sessionStorage.getItem('isAuthenticated') === 'true'
  );

  useEffect(() => {
    sessionStorage.setItem('isAuthenticated', isAuthenticated);
  }, [isAuthenticated]);

  // ─── Login con usuario y contraseña ────────────────────────────────────────
  const login = async (username, password) => {
    const response = await fetch(`${apiUrl}/api/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(data.message || data.mensaje || 'Credenciales incorrectas');
    }

    // Si requiere 2FA (código por correo)
    if (data.require2fa) {
      return data;
    }

    let { permisos = [], perfilID, perfilNombre, usuarioID, empresas = [], correo, accesos = {}, rutaInicial } = data;

    let permisosNormalizados = normalizePermisos(permisos);
    if (permisosNormalizados.includes(1) && !permisosNormalizados.includes(16)) {
      permisosNormalizados.push(16);
    }

    guardarSesion({ permisos: permisosNormalizados, perfilID, perfilNombre, usuarioID, empresas, correo, accesos, rutaInicial, loggedInUsername: username });
    setIsAuthenticated(true);

    return { ...data, rutaInicial: rutaInicial || '/Solicitudes', permisos: permisosNormalizados };
  };

  // ─── Verificar Código 2FA ──────────────────────────────────────────────────
  const verify2fa = async (tempToken, codigo, username) => {
    const response = await fetch(`${apiUrl}/api/login/verificar-2fa`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tempToken, codigo, username }),
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(data.message || data.mensaje || 'Código inválido o expirado');
    }

    let { permisos = [], perfilID, perfilNombre, usuarioID, empresas = [], correo, loggedInUsername, accesos = {}, rutaInicial } = data;

    let permisosNormalizados = normalizePermisos(permisos);
    if (permisosNormalizados.includes(1) && !permisosNormalizados.includes(16)) {
      permisosNormalizados.push(16);
    }

    guardarSesion({ permisos: permisosNormalizados, perfilID, perfilNombre, usuarioID, empresas, correo, accesos, rutaInicial, loggedInUsername: loggedInUsername || username || '' });
    setIsAuthenticated(true);

    return { ...data, rutaInicial: rutaInicial || '/Solicitudes', permisos: permisosNormalizados };
  };

  // ─── Reenviar Código 2FA ───────────────────────────────────────────────────
  const resend2fa = async (tempToken, username) => {
    const response = await fetch(`${apiUrl}/api/login/reenviar-2fa`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tempToken, username }),
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(data.message || data.mensaje || 'Error al reenviar código');
    }

    return data;
  };

  // ─── Login con Microsoft ──────────────────────────────────────────────────
  const loginWithMicrosoft = async (email, name) => {
    const response = await fetch(`${apiUrl}/api/auth/microsoft-login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, name }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || err.mensaje || 'No se pudo autenticar con Microsoft');
    }

    const userData = await response.json();

    let { permisos = [], perfilID, perfilNombre, usuarioID, empresas = [], correo, loggedInUsername, accesos = {}, rutaInicial } = userData;

    let permisosNormalizados = normalizePermisos(permisos);
    if (permisosNormalizados.includes(1) && !permisosNormalizados.includes(16)) {
      permisosNormalizados.push(16);
    }

    guardarSesion({ permisos: permisosNormalizados, perfilID, perfilNombre, usuarioID, empresas, correo, accesos, rutaInicial, loggedInUsername: loggedInUsername || email });
    setIsAuthenticated(true);

    return { ...userData, rutaInicial: rutaInicial || '/Solicitudes', permisos: permisosNormalizados };
  };

  // ─── Logout Limpio y Total ─────────────────────────────────────────────────
  const logout = () => {
    setIsAuthenticated(false);
    sessionStorage.clear();
    try {
      const accounts = msalInstance.getAllAccounts();
      accounts.forEach((acc) => {
        try {
          msalInstance.getTokenCache().removeAccount(acc);
        } catch (e) {}
      });
      msalInstance.setActiveAccount(null);
    } catch (e) {
      console.warn('Error clearing MSAL cache:', e);
    }
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, verify2fa, resend2fa, loginWithMicrosoft, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

// ─── Helpers ─────────────────────────────────────────────────────────────────
function normalizePermisos(permisos) {
  if (Array.isArray(permisos)) return permisos.map(Number).filter(n => !isNaN(n));
  if (typeof permisos === 'string') return permisos.split(',').map(p => Number(p.trim())).filter(n => !isNaN(n));
  if (typeof permisos === 'number') return [permisos];
  return [];
}

function guardarSesion({ permisos, perfilID, perfilNombre, usuarioID, empresas, correo, accesos, rutaInicial, loggedInUsername }) {
  sessionStorage.setItem('isAuthenticated', 'true');
  sessionStorage.setItem('Permisos', JSON.stringify(permisos));
  sessionStorage.setItem('PerfilID', JSON.stringify(perfilID || null));
  sessionStorage.setItem('PerfilNombre', JSON.stringify(perfilNombre || ''));
  sessionStorage.setItem('UsuarioId', JSON.stringify(usuarioID || null));
  sessionStorage.setItem('loggedInUsername', loggedInUsername || '');
  sessionStorage.setItem('correo', JSON.stringify(correo || ''));
  sessionStorage.setItem('RutaInicial', rutaInicial || '/Solicitudes');
  if (Array.isArray(empresas) && empresas.length > 0) {
    sessionStorage.setItem('EmpresaData', JSON.stringify(empresas));
  } else {
    sessionStorage.removeItem('EmpresaData');
  }
  if (accesos && typeof accesos === 'object') {
    sessionStorage.setItem('Accesos', JSON.stringify(accesos));
  } else {
    sessionStorage.removeItem('Accesos');
  }
}
